var Layout         = require("enyo-ui/Layout"),
    SlideVariables = require("app/theme/styles/slides/SlideVariables"),
    kind           = require("enyo/kind"),
    lerp           = require("app/utils/lerp"),
    themeVars      = require("app/theme/variables.js"),
    utils          = require("enyo/utils");

module.exports = kind(/** @lends  app.ui.HeaderGraphic.GraphicContainerLayout.prototype */ {

    /**
     * @private
     */
    name: "app.ui.HeaderGraphic.GraphicContainerLayout",

    /**
     * @private
     */
    kind: Layout,

    /**
     * The layout will expose some computed measurements here, for easy reuse by
     * nested components.
     *
     * @readonly
     * @public
     */
    metrics: null,

    /**
     * Default options for the layout.
     *
     * @type {Object}
     * @protected
     */
    options: /** @lends  app.ui.HeaderGraphic.GraphicContainerLayout.prototype */ {
        /**
         * The type of layout to apply. Supported values:
         *  - `classic`   The layout we traditionally use for sliders, swapping
         *                between 1:1, 3:2, 2:1, 3:1.
         *  - `overflow`  The new style layout where the graphic can overflow
         *                beyond the container.
         *
         * @returns  {string}  The layout style
         * @public
         */
        layoutStyle: function() {
            var layoutStyle;

            if (typeof this.container.resolveLayoutStyle === "function") {
                layoutStyle = this.container.resolveLayoutStyle();
            }

            switch (layoutStyle) {
                case "classic":
                case "overflow":
                    return layoutStyle;
                default:
                    return "classic";
            }
        }
    },

    /**
     * @public
     */
    reflow: function() {
        var $container  = this.container,
            layoutStyle,
            layoutMetrics;

        if (!$container.hasNode()) {
            return;
        }

        layoutStyle = this.getOption("layoutStyle");

        switch (layoutStyle) {
            case "overflow":
                layoutMetrics = this.overflowLayout();
                break;
            case "classic":
            default:
                layoutMetrics = this.classicLayout();
                break;
        }

        // Scale container and client
        $container.addStyles({
            height: layoutMetrics.containerHeight + "px"
        });
        $container.$.client.addStyles({
            width:  layoutMetrics.graphicWidth + "px",
            height: layoutMetrics.graphicHeight + "px"
        });
        $container.$.client.setAttribute("layout", layoutStyle);

        // Report layoutMetrics for other components to use
        $container.set("layoutMetrics", layoutMetrics);
    },

    /**
     * @returns  {Object}  The layout metrics
     * @private
     */
    classicLayout: function() {
        var $container          = this.container,
            formFactors         = SlideVariables.formFactors.default || {},
            appView             = $container.app.view,
            contentRegionBounds = appView.get("contentRegionBounds"),
            contentRegionHeight = contentRegionBounds.height,
            contentRegionWidth  = contentRegionBounds.width,
            contentRegionRatio  = contentRegionWidth / contentRegionHeight,
            bestFormFactor,
            graphicWidth,
            graphicHeight,
            containerWidth,
            containerHeight;

        // Sort by width descending
        formFactors.sort(function(a, b) {
            return a.width > b.width ? -1 : 1;
        });

        // Find the lowest resolution formFactor that fits the following
        // requirements:
        //  - it fills at least 75% of the height of the contentRegion
        //  - it fills at least 100% of the width of the contentRegion
        formFactors.forEach(function(formFactor) {
            // Form-factor fits requirements
            if (1 / formFactor.aspectRatio < (1 / contentRegionRatio) * 0.75 && formFactor.width >= contentRegionWidth) {
                bestFormFactor = formFactor;
            }
        });

        // No formFactors of sufficient resolution were found. Let's pick the
        // widest one we have instead. If the maximum width is shared among
        // multiple formFactors, pick the one that matches the desired aspect
        // ratio the the closest.
        if (!bestFormFactor) {
            formFactors.forEach(function(formFactor) {
                // Make a copy and calculate in which ways we're not living up
                // to the ideal specifications.
                formFactor                       = utils.clone(formFactor);
                formFactor.widthDeficit          = contentRegionWidth - formFactor.width;
                formFactor.aspectRatioDifference = Math.abs(contentRegionRatio * 0.75 - formFactor.aspectRatio);

                // First formFactor we encountered
                if (!bestFormFactor) {
                    bestFormFactor = formFactor;
                }
                else {
                    // The less missing width, the better
                    if (formFactor.widthDeficit < bestFormFactor.widthDeficit) {
                        bestFormFactor = formFactor;
                    }

                    // If width deficit is the same, the closest aspect ratio is
                    // better
                    else if (formFactor.widthDeficit === bestFormFactor.widthDeficit
                        && formFactor.aspectRatioDifference < bestFormFactor.aspectRatioDifference) {
                        bestFormFactor = formFactor;
                    }
                }
            });
        }

        // Calculate layoutMetrics
        graphicWidth    = contentRegionWidth;
        graphicHeight   = Math.round(graphicWidth / bestFormFactor.aspectRatio);
        containerWidth  = graphicWidth;
        containerHeight = graphicHeight;

        return {
            containerWidth:  containerWidth,
            containerHeight: containerHeight,
            graphicWidth:    graphicWidth,
            graphicHeight:   graphicHeight,
            graphicOverflow: 0
        };
    },

    /**
     * @returns  {Object}  The layout metrics
     * @private
     */
    overflowLayout: function() {
        var $container          = this.container,
            appView             = $container.app.view,
            headerRegionBounds  = appView.get("headerRegionBounds") || {},
            contentRegionBounds = appView.get("contentRegionBounds") || {},
            headerRegionHeight  = headerRegionBounds.height || 0,
            contentRegionWidth  = contentRegionBounds.width || 0,
            contentRegionHeight = contentRegionBounds.height || 0,
            headerOffset,
            graphicWidth,
            graphicHeight,
            containerWidth,
            containerHeight;

        // Larger screens
        if (["medium", "large"].includes(themeVars.formFactor)) {
            graphicWidth    = contentRegionWidth;
            graphicHeight   = Math.round(Math.max(
                480,
                Math.min(contentRegionWidth, contentRegionHeight) * 0.75
            ));
            containerWidth  = graphicWidth;
            containerHeight = Math.round(Math.max(360, Math.min(contentRegionWidth, contentRegionHeight) * 0.75 * 0.75));
        }

        // Small screens
        else {
            headerOffset    = themeVars.appHeaderFloat ? headerRegionHeight + themeVars.safeAreaInsetTop : 0;
            graphicWidth    = contentRegionWidth;
            graphicHeight   = graphicWidth;
            containerWidth  = graphicWidth;
            containerHeight = Math.round(Math.max(
                160 + headerOffset,
                lerp(
                    themeVars.windowAspectRatio,
                    0.618,
                    1,
                    contentRegionWidth / 4 * 3,
                    contentRegionWidth / 16 * 9
                )
            ));
        }

        return {
            containerWidth:  containerWidth,
            containerHeight: containerHeight,
            graphicWidth:    graphicWidth,
            graphicHeight:   graphicHeight,
            graphicOverflow: graphicHeight - containerHeight
        };
    }
});
